/**
 * SmartKargo identity layer. Only official artwork is used (vector files in /public/brand, unchanged):
 *   smartkargo-logo.svg        full logo, dark "Kargo" (light surfaces)
 *   smartkargo-logo-light.svg  full logo, white "Kargo" (dark surfaces)
 *   smartkargo-cloud.svg       the cloud mark on its own (path copied from the official artwork)
 * The product name and IT ownership sit beside the logo as text, never inside it.
 */

/** Full SmartKargo logo for the current theme (dark artwork in light mode, reversed in dark mode). */
export function SmartKargoLogo({ className = 'h-5' }: { className?: string }) {
  return <OfficialLogo className={className} />;
}

/** The SmartKargo cloud mark on its own (compact spaces: badges, loaders). */
export function SmartKargoMark({ className = 'size-6' }: { className?: string }) {
  return <img src="/brand/smartkargo-cloud.svg" alt="" aria-hidden className={`${className} object-contain`} draggable={false} />;
}

/**
 * Official SmartKargo logo with a one-off sheen: a soft highlight, masked to the logo's own shape, sweeps
 * across it on mount and again when the lockup is hovered. `tone="dark"` always uses the reversed artwork
 * (white "Kargo"); `tone="auto"` follows the theme.
 */
export function OfficialLogo({ tone = 'auto', className = 'h-[18px]' }: { tone?: 'dark' | 'auto'; className?: string }) {
  const light = '/brand/smartkargo-logo-light.svg';
  const dark = '/brand/smartkargo-logo.svg';
  const sheen = (src: string, extra = '') => (
    <span aria-hidden className={`sk-sheen pointer-events-none absolute inset-0 ${extra}`} style={{ maskImage: `url(${src})`, WebkitMaskImage: `url(${src})` }} />
  );
  return (
    <span className={`relative inline-block shrink-0 ${className}`} role="img" aria-label="SmartKargo">
      {tone === 'dark' ? (
        <>
          <img src={light} alt="" className="h-full w-auto" draggable={false} />
          {sheen(light)}
        </>
      ) : (
        <>
          <img src={dark} alt="" className="h-full w-auto dark:hidden" draggable={false} />
          <img src={light} alt="" className="hidden h-full w-auto dark:block" draggable={false} />
          {sheen(dark, 'dark:hidden')}
          {sheen(light, 'hidden dark:block')}
        </>
      )}
    </span>
  );
}

/**
 * Product line set flush under the logo: "SMART ASSETS · IT TEAM". The line slides in once; a warm shine
 * then crosses the product name (again on lockup hover). Pure text, so it keeps the logo's left edge.
 */
function ProductLine({ className, nameClass, teamClass }: { className: string; nameClass: string; teamClass: string }) {
  return (
    <span className={`sk-product flex items-center gap-2 leading-none font-semibold uppercase ${className}`}>
      <span aria-hidden className="sa-rule h-px w-4 shrink-0 bg-brand" />
      <span className={`sa-shine ${nameClass}`}>Smart Assets</span>
      <span aria-hidden className="size-[3px] shrink-0 rounded-full bg-current opacity-30" />
      <span className={teamClass}>IT Team</span>
    </span>
  );
}

/** Product lockup on dark surfaces (sidebar, sign-in): glowing SmartKargo logo over the product line. */
export function SidebarLockup({ size = 'md' }: { size?: 'md' | 'lg' }) {
  const lg = size === 'lg';
  return (
    <div className={`sk-lockup flex flex-col ${lg ? 'gap-3' : 'gap-2.5'}`}>
      <span className="sk-halo relative inline-flex w-fit">
        <OfficialLogo tone="dark" className={lg ? 'h-[38px]' : 'h-[28px]'} />
      </span>
      <ProductLine
        className={`text-white/40 ${lg ? 'text-[11px] tracking-[0.2em]' : 'text-[10px] tracking-[0.2em]'}`}
        nameClass="sa-on-dark"
        teamClass="text-[#ff8a57]"
      />
    </div>
  );
}

/** Same lockup for the light/dark top bar of the employee view (logo and text follow the theme). */
export function HeaderLockup() {
  return (
    <span className="sk-lockup flex items-center gap-3">
      <span className="sk-halo relative inline-flex">
        <OfficialLogo className="h-[24px]" />
      </span>
      <span aria-hidden className="h-6 w-px bg-line" />
      <span className="text-[11px] leading-tight font-semibold tracking-[0.16em] uppercase">
        <span className="sa-shine block">Smart Assets</span>
        <span className="block text-brand-ink">IT Team</span>
      </span>
    </span>
  );
}

/** Full-screen loading state: the SmartKargo cloud mark with its arrow lifting, then the product name. */
export function BrandLoader({ label = 'Loading SmartKargo Smart Assets' }: { label?: string }) {
  return (
    <div role="status" aria-label={label} className="grid min-h-dvh place-items-center bg-bg">
      <div className="flex flex-col items-center gap-4">
        <SmartKargoMark className="sk-loader h-12 w-[76px]" />
        <p className="eyebrow">Smart Assets</p>
      </div>
    </div>
  );
}
